/**
 * Copyright Mántum All Rights Reserved.
 */
import Active from '../../form/active';
import Autocomplete from '../../form/autocomplete';
import Batch from '../../form/batch';
import Chart from '../../form/chart';
import Checkbox from '../../form/checkbox';
import Chips from '../../form/chips';
import Color from '../../form/color';
import Country from '../../form/country';
import Currency from '../../form/currency';
import DataTable from '../../form/datatable';
import DateBox from '../../form/date';
import DateTime from '../../form/datetime';
import Email from '../../form/email';
import ExternalAction from '../../form/external-action';
import File from '../../form/file';
import Filter from '../button/filter';
import Hidden from '../../form/hidden';
import Image from '../../form/image';
import Label from '../../form/label';
import List from '../../form/list';
import Multiple from '../../form/multiple';
import NumberBox from '../../form/number';
import Password from '../../form/password';
import Radio from '../../form/radio';
import Reader from '../../form/reader';
import Select from '../../form/select';
import Skeleton from '../../form/skeleton';
import TermsAndConditions from '../../form/terms-and-conditions';
import Text from '../../form/text';
import Textarea from '../../form/textarea';
import Time from '../../form/time';
import debug from 'debug';
import { EVENT_UNMOUNT, useGroupDispatch } from '../../group';
import { KEY_DETAIL, KEY_EVENT } from './constant';
import { constants as fields } from '@mantum/kanela';

import {
    Fragment,
    forwardRef,
    useEffect,
    useId,
    useImperativeHandle,
    useRef
} from 'react';

import type { Placeholder } from '../../form/datetime';

import type {
    Detail,
    Error,
    Exclude,
    Field,
    HttpError,
    Mode,
    Option,
    Options,
    Type,
    Values
} from '@mantum/kanela';

type DateValue = string | Date | 'now';

export type PropTypes = {
    env?: Record<string, unknown>;
    field: Field;
    mode?: Mode;
    onChange?: (name?: string, value?: unknown) => void;
    onMessage?: (
        title: string,
        errors?: HttpError | string[] | Error,
        success?: boolean
    ) => void;
    onClear?: (exclude: Exclude) => void;
    onClick?: (name?: string, value?: unknown) => void;
    onUnmount?: (name?: string) => void;
    onListener?: (values?: Record<string, unknown>) => void;
    readonly?: boolean;
    values?: Record<string, unknown>;
};

const KEY_MODE = 'mode';
const KEY_READONLY = 'readonly';
const KEY_AUTOCOMPLETE = 'autocomplete';

const columnsVariants: Record<number, string> = {
    1: 'md:col-span-1 lg:col-span-1',
    2: 'md:col-span-2 lg:col-span-2',
    3: 'md:col-span-3 lg:col-span-3',
    4: 'md:col-span-2 lg:col-span-4'
};

const rowsVariants: Record<number, string> = {
    1: 'row-span-1',
    2: 'row-span-2',
    3: 'row-span-3',
    4: 'row-span-4'
};

const logger = debug('kanela');

export default forwardRef<Detail<unknown>, PropTypes>(
    function Field(props, ref) {
        // prettier-ignore
        const {
            onUnmount
        } = props

        // prettier-ignore
        const {
            name,
            spaces = 1,
            optional = true,
        } = props.field;

        const seed = useId();
        const dispatch = useGroupDispatch();

        const unmountRef = useRef(onUnmount);
        const inputRef = useRef<Detail<unknown>>(null);

        useImperativeHandle(
            ref,
            () => {
                return {
                    name: inputRef.current?.name,
                    validable: inputRef.current?.validable ?? true,
                    clear() {
                        inputRef.current?.clear();
                    },
                    validate() {
                        const result = inputRef.current?.validate();
                        if (result) {
                            return true;
                        }
                        return false;
                    },
                    value() {
                        return inputRef.current?.value;
                    },
                    refresh() {
                        if (inputRef.current && inputRef.current?.refresh) {
                            inputRef.current.refresh();
                        }
                    },
                    focus(value?: string) {
                        if (inputRef.current && inputRef.current?.focus) {
                            return inputRef.current?.focus(value);
                        }
                    }
                };
            },
            []
        );

        useEffect(() => {
            unmountRef.current = onUnmount;
        }, [onUnmount]);

        useEffect(() => {
            return () => {
                dispatch({
                    type: EVENT_UNMOUNT,
                    content: name
                });

                if (unmountRef.current) {
                    unmountRef.current(name);
                }
            };
        }, [name, dispatch]);

        const value =
            props.values && props.field.name in props.values
                ? props.values[props.field.name]
                : props.field.value;

        const parent = props.readonly !== undefined ? props.readonly : false;
        const event =
            KEY_EVENT in props.field ? props.field[KEY_EVENT] : undefined;

        const detail =
            KEY_DETAIL in props.field ? props.field[KEY_DETAIL] : undefined;

        let readonly =
            props.field.readonly !== undefined ? props.field.readonly : parent;

        if (props.mode && KEY_MODE in props.field) {
            const mode = props.field?.[KEY_MODE] as Record<string, unknown>;
            if (mode && props.mode in mode) {
                const properties = mode[props.mode] as Record<string, unknown>;
                if (properties && KEY_READONLY in properties) {
                    readonly = properties[KEY_READONLY] as boolean;
                }
            }
        }

        const autocomplete =
            props.field.advanced &&
            typeof props.field.advanced === 'object' &&
            KEY_AUTOCOMPLETE in props.field.advanced
                ? props.field.advanced?.[KEY_AUTOCOMPLETE]
                : undefined;

        const style = props.field.type !== 'hidden' ? '' : 'hidden';

        const onClick = (value: unknown) => {
            if (props.onClick) {
                props.onClick(name, value);
            }
        };

        const onRowAction = (name?: string, value?: unknown) => {
            if (props.onClick) {
                props.onClick(name, value);
            }
        };

        const component: Record<Type, JSX.Element> = {
            [fields.BATCH_TYPE]: (
                <Batch
                    {...props.field}
                    value={value as Record<string, string>}
                />
            ),

            [fields.CHART_TYPE]: <Chart {...props.field} />,

            [fields.FILTER_TYPE]: (
                <Filter
                    advanced={props.field.advanced}
                    onClear={props.onClear}
                />
            ),

            [fields.HIDDEN_TYPE]: (
                <Hidden
                    name={props.field.name}
                    onChange={props.onChange}
                    ref={inputRef}
                    value={value as string | number}
                />
            ),

            [fields.ACTIVE_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Active
                        advanced={props.field.advanced}
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as boolean}
                    />
                </Label>
            ),

            [fields.CHIPS_TYPE]: (
                <Chips
                    advanced={props.field.advanced}
                    dataSource={props.field.dataSource}
                    disabled={props.field.readonly}
                    label={props.field.label}
                    name={props.field.name}
                    onChange={props.onChange}
                    optional={optional}
                    ref={inputRef}
                    testid={props.field.testid}
                    validation={props.field.validation}
                    value={value as string | number | Values}
                />
            ),

            [fields.RADIO_TYPE]: (
                <Radio
                    advanced={props.field.advanced}
                    dataSource={props.field.dataSource}
                    disabled={props.field.readonly}
                    label={props.field.label}
                    name={props.field.name}
                    onChange={props.onChange}
                    optional={optional}
                    ref={inputRef}
                    testid={props.field.testid}
                    validation={props.field.validation}
                    value={value as string}
                />
            ),

            [fields.IMAGE_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Image
                        advanced={props.field.advanced}
                        dataSource={props.field.dataSource}
                        disabled={props.field.readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        ref={inputRef}
                        testid={props.field.testid}
                    />
                </Label>
            ),

            [fields.NUMBER_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <NumberBox
                        advanced={props.field.advanced}
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as number}
                    />
                </Label>
            ),

            [fields.TERMS_AND_CONDITIONS_TYPE]: (
                <TermsAndConditions
                    advanced={props.field.advanced}
                    disabled={readonly}
                    name={props.field.name}
                    onChange={props.onChange}
                    optional={optional}
                    placeholder={
                        props.values &&
                        `${props.field.name}-text` in props.values
                            ? (props.values[
                                  `${props.field.name}-text`
                              ] as string)
                            : props.field.placeholder
                    }
                    ref={inputRef}
                    testid={props.field.testid}
                    validation={props.field.validation}
                    value={value as boolean}
                />
            ),

            [fields.CHECKBOX_TYPE]: (
                <Checkbox
                    disabled={readonly}
                    label={props.field.label}
                    name={props.field.name}
                    onChange={props.onChange}
                    optional={optional}
                    ref={inputRef}
                    testid={props.field.testid}
                    validation={props.field.validation}
                    value={value as boolean}
                />
            ),

            [fields.SKELETON_TYPE]: (
                <div className='flex flex-col w-full'>
                    <div className='h-5 w-1/2 bg-slate-200 animate-pulse mb-2'></div>
                    <Skeleton testid={props.field.testid} />
                </div>
            ),

            [fields.CURRENCY_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Currency
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as number}
                    />
                </Label>
            ),

            [fields.DATA_TABLE_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <DataTable
                        advanced={props.field.advanced}
                        columns={props.field.column}
                        dataSource={props.field.dataSource}
                        detail={detail}
                        name={props.field.name}
                        onChange={props.onChange}
                        onClick={onClick}
                        onRowAction={onRowAction}
                        optional={optional}
                        readonly={readonly}
                        ref={inputRef}
                        summaries={props.field.summaries}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as unknown[]}
                    />
                </Label>
            ),

            [fields.DATA_TABLE_SIMPLE_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <List
                        {...props.field}
                        onChange={props.onChange}
                        ref={inputRef}
                    />
                </Label>
            ),

            [fields.EMAIL_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Email
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as string}
                    />
                </Label>
            ),

            [fields.MULTIPLE_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Multiple
                        advanced={props.field.advanced}
                        dataSource={props.field.dataSource}
                        disabled={readonly}
                        instanceId={seed}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as Options<unknown>}
                    />
                </Label>
            ),

            [fields.SELECT_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Select
                        advanced={props.field.advanced}
                        dataSource={props.field.dataSource}
                        disabled={readonly}
                        event={event}
                        instanceId={seed}
                        name={props.field.name}
                        onChange={props.onChange}
                        onMessage={props.onMessage}
                        onListener={props.onListener}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as Option<unknown>}
                    />
                </Label>
            ),

            [fields.AUTOCOMPLETE_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Autocomplete
                        advanced={props.field.advanced}
                        dataSource={props.field.dataSource}
                        disabled={readonly}
                        instanceId={seed}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as Option<unknown>}
                    />
                </Label>
            ),

            [fields.READER_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Reader
                        advanced={props.field.advanced}
                        dataSource={props.field.dataSource}
                        disabled={readonly}
                        instanceId={seed}
                        name={props.field.name}
                        onChange={props.onChange}
                        onClick={props.onClick}
                        onMessage={props.onMessage}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as Option<unknown>}
                    />
                </Label>
            ),

            [fields.TEXT_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Text
                        autocomplete={autocomplete}
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as string}
                    />
                </Label>
            ),

            [fields.PASSWORD_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Password
                        autocomplete={autocomplete}
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as string}
                    />
                </Label>
            ),

            [fields.TEXT_AREA_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Textarea
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as string}
                    />
                </Label>
            ),

            [fields.COUNTRY_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Country
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as Option<unknown>}
                    />
                </Label>
            ),

            [fields.TIME_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Time
                        advanced={props.field.advanced}
                        disabled={readonly}
                        format={props.field.format}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as DateValue}
                    />
                </Label>
            ),

            [fields.DATE_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <DateBox
                        advanced={props.field.advanced}
                        disabled={readonly}
                        format={props.field.format}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as DateValue}
                        message={props.field.message}
                    />
                </Label>
            ),

            [fields.FILE_TYPE]: (
                <File
                    advanced={props.field.advanced}
                    dataSource={props.field.dataSource}
                    label={props.field.label}
                    name={props.field.name}
                    onChange={props.onChange}
                    optional={optional}
                    placeholder={props.field.placeholder}
                    readonly={readonly}
                    ref={inputRef}
                    testid={props.field.testid}
                    validation={props.field.validation}
                />
            ),

            [fields.DATE_TIME_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <DateTime
                        placeholder={props.field.placeholder as Placeholder}
                        advanced={props.field.advanced}
                        disabled={readonly}
                        format={props.field.format}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as string | Date | 'now'}
                    />
                </Label>
            ),

            [fields.COLOR_TYPE]: (
                <Label
                    disabled={readonly}
                    name={props.field.label}
                    optional={optional}
                >
                    <Color
                        advanced={props.field.advanced}
                        disabled={readonly}
                        name={props.field.name}
                        onChange={props.onChange}
                        optional={optional}
                        placeholder={props.field.placeholder}
                        ref={inputRef}
                        testid={props.field.testid}
                        validation={props.field.validation}
                        value={value as string}
                    />
                </Label>
            ),

            [fields.EXTERNAL_ACTION_TYPE]: (
                <ExternalAction
                    name={props.field.name}
                    value={value as string}
                    disabled={readonly}
                    advanced={props.field.advanced}
                    onClick={onClick}
                >
                    {props.field.label}
                </ExternalAction>
            )
        };

        const rows = props.field.column ? rowsVariants[props.field.column] : '';

        logger('create field component "%s": %o', name, value);
        return (
            <div
                className={`col-span-2 ${columnsVariants[spaces]} ${rows} ${style}`}
            >
                {component[props.field.type] || <Fragment />}
            </div>
        );
    }
);
